import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DollarSign, TrendingUp, RotateCcw, AlertTriangle } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DatePickerButton } from '@/components/ui/date-picker-button';

type GPSummaryMetricRow = {
  brand_id: number | null;
  brand_name: string | null;
  group_name: string | null;
  product_id: number[] | null;
  truth_net_sum: number | null;
  truth_total_sales: number | null;
  truth_total_cost: number | null;
  truth_total_discount: number | null;
};

type GPSummaryResponse = {
  start_datetime: string;
  end_datetime: string;
  brand_ids: number[];
  metrics: GPSummaryMetricRow[];
};

type UiRow = {
  key: string;
  name: string;
  brandId: number | null;
  // raw fields
  sales: number; // truth_total_sales
  discount: number; // truth_total_discount
  netSales: number; // sales - discount
  cost: number; // truth_total_cost
  gp: number; // truth_net_sum
  margin: number; // gp / netSales
  groupName: string | null;
  productIds: number[] | null;
};

function n(v: number | null | undefined) {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function fmtMoney(value: number) {
  try {
    return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR', maximumFractionDigits: 2 }).format(value);
  } catch {
    return `RM ${value.toFixed(2)}`;
  }
}

function fmtCompactMoney(value: number) {
  try {
    // "RM 1.2M" style
    const compact = new Intl.NumberFormat('en-MY', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
    return `RM ${compact}`;
  } catch {
    return `RM ${Math.round(value).toLocaleString()}`;
  }
}

function fmtPct(value: number) {
  if (!Number.isFinite(value)) return '0.0%';
  return `${(value * 100).toFixed(1)}%`;
}

function trimAxisLabel(label: string, maxLen = 7) {
  const s = String(label ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1)}…`;
}

type Viewport = 'mobile' | 'tablet' | 'desktop';

function getViewport(): Viewport {
  // Match Tailwind default breakpoints: md=768, lg=1024
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w >= 1024) return 'desktop';
  if (w >= 768) return 'tablet';
  return 'mobile';
}

export function GrossProfitPage() {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GPSummaryResponse | null>(null);
  const [viewport, setViewport] = useState<Viewport>(() => getViewport());

  useEffect(() => {
    const onResize = () => setViewport(getViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const canApply = Boolean(startDate && endDate);

  async function handleApply() {
    if (!canApply || loading) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/gp-summary/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      const json = (await resp.json()) as GPSummaryResponse;
      setData(json);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : 'Failed to load gp-summary.');
    } finally {
      setLoading(false);
    }
  }

  // Calculate totals
  const uiRows = useMemo<UiRow[]>(() => {
    const metrics = data?.metrics ?? [];
    return metrics.map((r, idx) => {
      const name =
        (r.brand_id != null ? r.brand_name : null) ??
        r.group_name ??
        (r.brand_id != null ? `Brand ${r.brand_id}` : `Row ${idx + 1}`);

      const sales = n(r.truth_total_sales);
      const discount = n(r.truth_total_discount);
      const netSales = sales - discount;
      const gp = n(r.truth_net_sum);
      const cost = n(r.truth_total_cost);
      const margin = netSales !== 0 ? gp / netSales : 0;

      const key =
        r.brand_id != null
          ? `brand:${r.brand_id}`
          : `group:${r.group_name ?? 'null'}:${idx}`;

      return {
        key,
        name,
        brandId: r.brand_id,
        sales,
        discount,
        netSales,
        cost,
        gp,
        margin,
        groupName: r.group_name,
        productIds: r.product_id,
      };
    });
  }, [data]);

  const brandRows = useMemo(() => uiRows.filter((r) => r.brandId != null), [uiRows]);
  const brandRowsExcludingGiftCard = useMemo(
    () => brandRows.filter((r) => r.brandId !== 2),
    [brandRows]
  );
  const rowsForTotals = useMemo(() => {
    // Include: brand rows + special product groups
    // Exclude: gift card row (brand_id=2) and free_gift aggregate row (group_name="free_gift")
    return uiRows.filter((r) => r.brandId !== 2 && r.groupName !== 'free_gift');
  }, [uiRows]);

  const totals = useMemo(() => {
    // Per requirement: sum every brand INCLUDING special product groups,
    // and exclude out only gift card (brand_id=2) + free_gift (group_name="free_gift").
    const netSales = rowsForTotals.reduce((sum, r) => sum + r.netSales, 0);
    const cost = rowsForTotals.reduce((sum, r) => sum + r.cost, 0);
    const gp = rowsForTotals.reduce((sum, r) => sum + r.gp, 0);
    const margin = netSales !== 0 ? gp / netSales : 0;

    return { netSales, cost, gp, margin };
  }, [rowsForTotals]);

  const chartData = useMemo(() => {
    const limit = viewport === 'desktop' ? Infinity : viewport === 'tablet' ? 8 : 5;
    // Exclude Gift Card (brand_id=2) and any non-brand rows (already filtered out).
    const sorted = brandRowsExcludingGiftCard
      .slice()
      .sort((a, b) => b.gp - a.gp)

    return sorted.slice(0, limit).map((r) => ({
      name: r.name,
      grossProfit: r.gp,
    }));
  }, [brandRowsExcludingGiftCard, viewport]);

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setData(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Filter Card */}
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Filters</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Select date range and brand to analyze gross profit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            {/* Start Date */}
            <div className="space-y-2 max-w-xs flex-1 min-w-[200px]">
              <Label className="text-sm text-gray-900 dark:text-neutral-50">Start Date</Label>
              <DatePickerButton
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a date"
              />
            </div>

            {/* End Date */}
            <div className="space-y-2 max-w-xs flex-1 min-w-[200px]">
              <Label className="text-sm text-gray-900 dark:text-neutral-50">End Date</Label>
              <DatePickerButton
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick a date"
              />
            </div>

            {/* Brand Select */}
            {/* Action Buttons */}
            <div className="flex gap-2 items-end flex-wrap">
              <Button
                className="bg-gray-900 dark:bg-neutral-50 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-neutral-100"
                disabled={!canApply || loading}
                onClick={handleApply}
              >
                {loading ? 'Loading...' : 'Apply'}
              </Button>
              <Button 
                variant="outline" 
                className="border-gray-200 dark:border-neutral-700"
                onClick={handleReset}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sales (net) Card */}
        <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <CardHeader className="flex flex-row items-start justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-neutral-50">Total Sales (Net)</CardTitle>
            <DollarSign className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-neutral-50">
              {fmtCompactMoney(totals.netSales)}
            </p>
            <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">Total sales after discount</p>
          </CardContent>
        </Card>

        {/* Total Cost Card */}
        <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <CardHeader className="flex flex-row items-start justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-neutral-50">Total Cost</CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-neutral-50">
              {fmtCompactMoney(totals.cost)}
            </p>
            <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">Cost of goods sold</p>
          </CardContent>
        </Card>

        {/* Gross Profit Card */}
        <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <CardHeader className="flex flex-row items-start justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-neutral-50">Gross Profit</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {fmtCompactMoney(totals.gp)}
            </p>
            <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">Revenue minus cost</p>
          </CardContent>
        </Card>

        {/* GP Margin Card */}
        <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <CardHeader className="flex flex-row items-start justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-neutral-50">GP Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{fmtPct(totals.margin)}</p>
            <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">Profit margin percentage</p>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Card className="border-red-200 dark:border-red-900 bg-white dark:bg-neutral-950">
          <CardHeader>
            <CardTitle className="text-base text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Failed to load
            </CardTitle>
            <CardDescription className="text-red-700/80 dark:text-red-400/80">
              {error}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Bar Chart Card */}
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Gross Profit by Brand</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Visual comparison of gross profit across brands
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(0,0,0,0.1)" 
                  className="dark:stroke-neutral-700"
                />
                <XAxis 
                  dataKey="name" 
                  stroke="rgb(107, 114, 128)"
                  className="dark:stroke-neutral-500"
                  tickFormatter={(v) => trimAxisLabel(String(v))}
                  interval={0}
                  minTickGap={8}
                />
                <YAxis 
                  stroke="rgb(107, 114, 128)"
                  className="dark:stroke-neutral-500"
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid rgb(229, 231, 235)',
                    borderRadius: '8px',
                    color: 'rgb(17, 24, 39)',
                  }}
                />
                <Legend />
                <Bar dataKey="grossProfit" fill="#3b82f6" name="Gross Profit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Breakdown Table */}
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Detailed Breakdown</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Gross Profit by Brand. **The final amount excludes Gift Card and Free Gift**
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900/50">
                  <TableHead className="px-4 py-3 text-gray-900 dark:text-neutral-50 font-semibold">Name</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Sales</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Discount</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Net Sales</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Cost</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Gross Profit</TableHead>
                  <TableHead className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50 font-semibold">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uiRows.map((r) => (
                  <TableRow key={r.key} className="border-b border-gray-200 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-900/30">
                    <TableCell className="px-4 py-3 text-gray-900 dark:text-neutral-50 font-medium">
                      <div className="flex flex-col gap-0.5">
                        <span className="truncate">{r.name}</span>
                        {/* For special group rows, only show group name (no extra details). */}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                      {fmtMoney(r.sales)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                      {fmtMoney(r.discount)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                      {fmtMoney(r.netSales)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                      {fmtMoney(r.cost)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-neutral-50">
                      {fmtMoney(r.gp)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <Badge className="bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-50 border-gray-200 dark:border-neutral-700">
                        {fmtPct(r.margin)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-gray-50 dark:bg-neutral-900/50 font-bold">
                  <TableCell className="px-4 py-3 text-gray-900 dark:text-neutral-50">Total</TableCell>
                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50" />
                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50" />
                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                    {fmtMoney(totals.netSales)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-neutral-50">
                    {fmtMoney(totals.cost)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right text-green-600 dark:text-green-400">
                    {fmtMoney(totals.gp)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <Badge className="bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-50 border-gray-200 dark:border-neutral-700">
                      {fmtPct(totals.margin)}
                    </Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
